import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { getTokenRedirect } from '../../utils/authRedirect';
import { tokenRequest } from '../../authConfig';
import { ragSearchAPI, RAGSearchParams, RAGSearchResult, RAGSearchResponse, RAGSourceType, RAGElargissement } from '../../services/ragSearchAPI';
import { LOADING_STATES } from '../../constants';
import type { RootState } from '../../app/store';

// ─── Types ────────────────────────────────────────────────────────────

/** Catégories de mentions contextuelles @ */
export type MentionCategory = 'sourceType' | 'perimetre' | 'perimetreNotes' | 'perimetreDocuments' | 'thesaurus' | 'note' | 'entree' | 'entreeShare';

/** Un item de contexte injecté par @mention */
export interface SearchMention {
    /** Identifiant unique (ex: 'source:note', 'perim:abc123', 'note:xyz') */
    id: string;
    /** Catégorie */
    category: MentionCategory;
    /** Libellé affiché dans le chip (ex: 'Note', 'Philosophie', 'Mon thésaurus') */
    label: string;
    /** Valeur identifiant (ex: id du périmètre, id de la note) */
    value: string;
    /** Texte de contexte à injecter dans le prompt (description, contenu, etc.) */
    contextText: string;
    /** Couleur du chip */
    color: string;
    /** Couleur de fond du chip */
    bgColor: string;
    /** URL du fichier blob (pour perimetreDocuments) */
    fileUrl?: string;
    /** Nom du fichier (pour perimetreDocuments) */
    fileName?: string;
    /** Type MIME du fichier (pour perimetreDocuments) */
    mimeType?: string;
}

/** Items de contexte envoyés à l'API */
export interface ContextItem {
    type: MentionCategory;
    label: string;
    content: string;
    value?: string;
    /** URL du fichier blob (pour perimetreDocuments) */
    fileUrl?: string;
    /** Nom du fichier (pour perimetreDocuments) */
    fileName?: string;
    /** Type MIME du fichier (pour perimetreDocuments) */
    mimeType?: string;
}

export interface RAGSearchThunkParams extends RAGSearchParams {
    /** Instance MSAL pour l'authentification */
    instance: any;
}

/** Demande d'ouverture de modal depuis les résultats RAG */
export interface PendingModalOpen {
    type: RAGSourceType;
    id: string;
    title?: string;
    result: RAGSearchResult;
}

export interface RAGSearchState {
    /** Requête saisie par l'utilisateur */
    query: string;
    /** Résultats de la recherche */
    results: RAGSearchResult[];
    /** État de chargement */
    status: string;
    /** Message d'erreur */
    error?: string;
    /** Mode de recherche actif */
    mode: 'hybrid' | 'vector' | 'fulltext';
    /** Nombre total de résultats retournés */
    totalResults: number;
    /** Durée de la recherche (ms) */
    duration: number;
    /** Panneau de recherche ouvert/fermé */
    isOpen: boolean;
    /** Historique des recherches récentes (max 10) */
    recentQueries: string[];
    /** Demande d'ouverture de modal (consommé par MainPage) */
    pendingModalOpen: PendingModalOpen | null;
    /** Mentions contextuelles @ actives (injectées comme contexte dans le prompt) */
    mentions: SearchMention[];
    /**
     * Renvois USE appliqués à la dernière recherche (« automobile » → « Voiture »).
     * Montrés à l'utilisateur : la recherche a cherché autre chose que ce qu'il a tapé.
     */
    elargissements: RAGElargissement[];
    /**
     * `false` quand l'utilisateur a demandé à chercher ses termes exacts. Conservé pour
     * que la recherche suivante respecte son choix jusqu'à ce qu'il le change.
     */
    elargirRenvois: boolean;
}

const initialState: RAGSearchState = {
    query: '',
    results: [],
    status: LOADING_STATES.IDLE,
    error: undefined,
    mode: 'hybrid',
    totalResults: 0,
    duration: 0,
    isOpen: false,
    recentQueries: [],
    pendingModalOpen: null,
    mentions: [],
    elargissements: [],
    elargirRenvois: true,
};

// ─── Async Thunks ─────────────────────────────────────────────────────

/**
 * Lance une recherche RAG sémantique
 */
export const executeRAGSearch = createAsyncThunk<
    RAGSearchResponse,
    RAGSearchThunkParams,
    { state: RootState; rejectValue: string }
>(
    'ragSearch/execute',
    async ({ instance, ...params }, { rejectWithValue }) => {
        try {
            const token = await getTokenRedirect(tokenRequest, instance);
            if (!token) {
                return rejectWithValue('Impossible d\'obtenir le token d\'authentification');
            }

            const response = await ragSearchAPI.search(params, token);
            return response;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Erreur de recherche');
        }
    }
);

// ─── Slice ────────────────────────────────────────────────────────────

const ragSearchSlice = createSlice({
    name: 'ragSearch',
    initialState,
    reducers: {
        /** Met à jour la query en cours de saisie */
        setQuery(state, action: PayloadAction<string>) {
            state.query = action.payload;
        },
        /** Ouvre/ferme le panneau de recherche RAG */
        toggleSearchPanel(state) {
            state.isOpen = !state.isOpen;
        },
        setSearchPanelOpen(state, action: PayloadAction<boolean>) {
            state.isOpen = action.payload;
        },
        /** Change le mode de recherche */
        setSearchMode(state, action: PayloadAction<'hybrid' | 'vector' | 'fulltext'>) {
            state.mode = action.payload;
        },
        /** Supprime les résultats */
        clearResults(state) {
            state.results = [];
            state.totalResults = 0;
            state.duration = 0;
            state.status = LOADING_STATES.IDLE;
            state.error = undefined;
        },
        /** Réinitialise complètement la recherche */
        resetSearch(state) {
            state.query = '';
            state.results = [];
            state.totalResults = 0;
            state.duration = 0;
            state.status = LOADING_STATES.IDLE;
            state.error = undefined;
            state.pendingModalOpen = null;
            state.mentions = [];
        },
        /** Demande l'ouverture d'un modal pour un résultat cliqué */
        setPendingModalOpen(state, action: PayloadAction<PendingModalOpen>) {
            state.pendingModalOpen = action.payload;
        },
        /** Efface la demande d'ouverture de modal (après traitement par MainPage) */
        clearPendingModalOpen(state) {
            state.pendingModalOpen = null;
        },
        /** Ajoute une mention contextuelle @ */
        addMention(state, action: PayloadAction<SearchMention>) {
            // Éviter les doublons par id
            if (!state.mentions.find(m => m.id === action.payload.id)) {
                state.mentions.push(action.payload);
            }
        },
        /** Supprime une mention par id */
        removeMention(state, action: PayloadAction<string>) {
            state.mentions = state.mentions.filter(m => m.id !== action.payload);
        },
        /** Efface toutes les mentions */
        clearMentions(state) {
            state.mentions = [];
        },
        /**
         * Active ou coupe les renvois USE du thésaurus. Couper revient à demander les
         * termes exacts saisis ; les élargissements affichés deviennent caducs.
         */
        setElargirRenvois(state, action: PayloadAction<boolean>) {
            state.elargirRenvois = action.payload;
            if (!action.payload) state.elargissements = [];
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(executeRAGSearch.pending, (state) => {
                state.status = LOADING_STATES.LOADING;
                state.error = undefined;
            })
            .addCase(executeRAGSearch.fulfilled, (state, action) => {
                state.status = LOADING_STATES.SUCCEEDED;
                state.results = action.payload.results;
                state.totalResults = action.payload.totalFound ?? action.payload.results.length;
                state.duration = action.payload.duration;
                state.mode = action.payload.mode as any;
                state.elargissements = action.payload.elargissements ?? [];
                // Ajouter à l'historique
                const query = action.payload.query;
                if (query && !state.recentQueries.includes(query)) {
                    state.recentQueries = [query, ...state.recentQueries].slice(0, 10);
                }
            })
            .addCase(executeRAGSearch.rejected, (state, action) => {
                state.status = LOADING_STATES.FAILED;
                state.error = action.payload || 'Erreur inconnue';
                state.results = [];
                state.totalResults = 0;
            });
    }
});

// ─── Exports ──────────────────────────────────────────────────────────

export const {
    setQuery,
    toggleSearchPanel,
    setSearchPanelOpen,
    setSearchMode,
    clearResults,
    resetSearch,
    setPendingModalOpen,
    clearPendingModalOpen,
    addMention,
    removeMention,
    clearMentions,
    setElargirRenvois,
} = ragSearchSlice.actions;

// Selectors
export const selectRAGElargissements = (state: RootState) => state.ragSearch.elargissements;
export const selectRAGElargirRenvois = (state: RootState) => state.ragSearch.elargirRenvois;
export const selectRAGQuery = (state: RootState) => state.ragSearch.query;
export const selectRAGResults = (state: RootState) => state.ragSearch.results;
export const selectRAGStatus = (state: RootState) => state.ragSearch.status;
export const selectRAGError = (state: RootState) => state.ragSearch.error;
export const selectRAGIsOpen = (state: RootState) => state.ragSearch.isOpen;
export const selectRAGMode = (state: RootState) => state.ragSearch.mode;
export const selectRAGDuration = (state: RootState) => state.ragSearch.duration;
export const selectRAGTotalResults = (state: RootState) => state.ragSearch.totalResults;
export const selectRAGRecentQueries = (state: RootState) => state.ragSearch.recentQueries;
export const selectPendingModalOpen = (state: RootState) => state.ragSearch.pendingModalOpen;
export const selectRAGMentions = (state: RootState) => state.ragSearch.mentions;

export default ragSearchSlice.reducer;
